import React, { useMemo, useState } from 'react';
import { PageLayout } from '../../shared/PageLayout';
import { StatCard } from '../../shared/StatCard';
import { DataTable, Column } from '../shared/DataTable';
import { MasteryBar } from '../shared/MasteryBar';
import { ConnectionBadge } from '../shared/ConnectionBadge';
import { Badge } from '../../../components/ui/Badge';
import { Card } from '../../../components/ui/Card';
import { Input } from '../../../components/ui/Input';
import { Select } from '../../../components/ui/Select';
import { CheckCircle2, XCircle, Search, Filter, BookOpen, FileText, X, Lightbulb, AlertTriangle } from 'lucide-react';
import { TAXONOMY, QUESTIONS_BY_FAMILY } from '../../../content';
import { resolveUnifiedSupport, FAMILY_SUPPORT_REGISTRY, type ResolvedSupport, type HintStep } from '../../../support';
interface FamilySupportRow {
  id: string;
  familyId: string;
  module: string;
  tier: number;
  questionCount: number;
  support: ResolvedSupport;
  completeness: number;
  hintCount: number;
  registryHints: HintStep[];
  inRegistry: boolean;
}
export function SupportContentPage() {
  const [searchQuery, setSearchQuery] = useState('');
  const [moduleFilter, setModuleFilter] = useState<string>('all');
  const [coverageFilter, setCoverageFilter] = useState<'all' | 'full' | 'partial' | 'none'>('all');
  const [hintFilter, setHintFilter] = useState<'all' | 'enriched' | 'sparse' | 'none'>('all');
  const [selectedFamily, setSelectedFamily] = useState<FamilySupportRow | null>(null);
  const allData: FamilySupportRow[] = useMemo(() => {
    return TAXONOMY.map(family => {
      const support = resolveUnifiedSupport(family.familyId);
      const registryEntry = FAMILY_SUPPORT_REGISTRY[family.familyId];
      const registryHints = registryEntry?.hints || [];
      return {
        id: family.familyId,
        familyId: family.familyId,
        module: family.module,
        tier: family.tier,
        questionCount: QUESTIONS_BY_FAMILY.get(family.familyId)?.length ?? 0,
        support,
        completeness: support.coverage.completeness,
        hintCount: registryHints.length,
        registryHints,
        inRegistry: !!registryEntry
      };
    });
  }, []);
  const stats = useMemo(() => {
    const total = allData.length;
    const full = allData.filter(d => d.completeness === 100).length;
    const partial = allData.filter(d => d.completeness > 0 && d.completeness < 100).length;
    const none = allData.filter(d => d.completeness === 0).length;
    const inRegistry = allData.filter(d => d.inRegistry).length;
    const enrichedHints = allData.filter(d => d.hintCount >= 2).length;
    const sparseHints = allData.filter(d => d.hintCount === 1).length;
    const noHints = allData.filter(d => d.hintCount === 0).length;
    const totalHints = allData.reduce((sum, d) => sum + d.hintCount, 0);
    const overallCoverage = Math.round(inRegistry / total * 100);
    return {
      total,
      full,
      partial,
      none,
      inRegistry,
      enrichedHints,
      sparseHints,
      noHints,
      totalHints,
      overallCoverage
    };
  }, [allData]);
  const moduleStats = useMemo(() => {
    const modules = Array.from(new Set(allData.map(d => d.module))).sort();
    return modules.map(mod => {
      const moduleData = allData.filter(d => d.module === mod);
      const familyCount = moduleData.length;
      const avgCompleteness = Math.round(moduleData.reduce((sum, d) => sum + d.completeness, 0) / familyCount);
      const totalHints = moduleData.reduce((sum, d) => sum + d.hintCount, 0);
      return {
        name: mod,
        familyCount,
        avgCompleteness,
        totalHints
      };
    });
  }, [allData]);
  const filteredData = useMemo(() => {
    return allData.filter(row => {
      if (searchQuery && !row.familyId.toLowerCase().includes(searchQuery.toLowerCase())) return false;
      if (moduleFilter !== 'all' && row.module !== moduleFilter) return false;
      if (coverageFilter === 'full' && row.completeness !== 100) return false;
      if (coverageFilter === 'partial' && (row.completeness === 0 || row.completeness === 100)) return false;
      if (coverageFilter === 'none' && row.completeness !== 0) return false;
      if (hintFilter === 'enriched' && row.hintCount < 2) return false;
      if (hintFilter === 'sparse' && row.hintCount !== 1) return false;
      if (hintFilter === 'none' && row.hintCount !== 0) return false;
      return true;
    });
  }, [allData, searchQuery, moduleFilter, coverageFilter, hintFilter]);
  const modules = useMemo(() => Array.from(new Set(TAXONOMY.map(f => f.module))).sort(), []);
  const exampleQuestion = useMemo(() => {
    if (!selectedFamily) return null;
    const questions = QUESTIONS_BY_FAMILY.get(selectedFamily.familyId);
    return questions && questions.length > 0 ? questions[0] : null;
  }, [selectedFamily]);
  const renderCheck = (hasItem: boolean) => {
    return hasItem ? <CheckCircle2 className="w-4 h-4 text-emerald-500" data-id="element-3490" /> : <XCircle className="w-4 h-4 text-gray-300" data-id="element-3491" />;
  };
  const renderHintCount = (count: number) => {
    if (count === 0) {
      return <span className="inline-flex items-center gap-1 text-xs text-gray-400" data-id="element-3492">
          <XCircle className="w-3.5 h-3.5" data-id="element-3493" />
          <span data-id="element-3494">0</span>
        </span>;
    }
    if (count === 1) {
      return <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-amber-50 text-amber-700 text-xs font-medium border border-amber-200" data-id="element-3495">
          <AlertTriangle className="w-3 h-3" data-id="element-3496" />
          <span data-id="element-3497">1</span>
        </span>;
    }
    return <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-emerald-50 text-emerald-700 text-xs font-medium border border-emerald-200" data-id="element-3498">
        <Lightbulb className="w-3 h-3" data-id="element-3499" />
        <span data-id="element-3500">{count}</span>
      </span>;
  };
  const columns: Column<FamilySupportRow>[] = [{
    key: 'familyId',
    label: 'Family ID',
    render: val => <span className="font-mono text-xs font-medium text-gray-700" data-id="element-3501">
          {val}
        </span>
  }, {
    key: 'module',
    label: 'Domein',
    render: val => <Badge variant="secondary" data-id="element-3502">{val}</Badge>
  }, {
    key: 'tier',
    label: 'Tier',
    render: val => <Badge variant="secondary" data-id="element-3503">T{val}</Badge>
  }, {
    key: 'questionCount',
    label: 'Vragen',
    render: val => <span className="text-xs font-medium text-gray-600" data-id="element-3504">{val}</span>
  }, {
    key: 'explanation',
    label: 'Uitleg',
    render: (_, row) => renderCheck(row.support.coverage.hasExplanation)
  }, {
    key: 'cheatSheet',
    label: 'Spiekbriefje',
    render: (_, row) => renderCheck(row.support.coverage.hasCheatSheet)
  }, {
    key: 'hintCount',
    label: 'Hints',
    render: val => renderHintCount(val)
  }, {
    key: 'steps',
    label: 'Stappen',
    render: (_, row) => renderCheck(row.support.coverage.hasSteps)
  }, {
    key: 'completeness',
    label: 'Compleetheid',
    render: val => <MasteryBar value={val} data-id="element-3505" />
  }];
  // Helper for module colors
  const getModuleColor = (index: number) => {
    const colors = ['border-blue-500', 'border-purple-500', 'border-emerald-500', 'border-amber-500', 'border-rose-500', 'border-cyan-500', 'border-indigo-500', 'border-orange-500', 'border-teal-500', 'border-pink-500'];
    return colors[index % colors.length];
  };
  return <PageLayout title="Ondersteuning Content" subtitle="Uitleg, hints en spiekbriefjes per family — compleet overzicht van alle ondersteuningscontent." actions={<ConnectionBadge status="live" data-id="element-3507" />} breadcrumbs={[{
    label: 'Inhoud'
  }, {
    label: 'Ondersteuning'
  }]} data-id="element-3506">
      {/* Module Summary Cards */}
      <div className="mb-6" data-id="element-3508">
        <h3 className="text-sm font-semibold text-gray-900 mb-3" data-id="element-3509">
          Status per Domein
        </h3>
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3" data-id="element-3510">
          {moduleStats.map((mod, idx) => <Card key={mod.name} className={`p-3 border-l-4 ${getModuleColor(idx)}`} data-id="element-3511">
              <div className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-1" data-id="element-3512">
                {mod.name}
              </div>
              <div className="flex items-end justify-between" data-id="element-3513">
                <div data-id="element-3514">
                  <div className="text-lg font-bold text-gray-900" data-id="element-3515">
                    {mod.avgCompleteness}%
                  </div>
                  <div className="text-xs text-gray-500" data-id="element-3516">Compleet</div>
                </div>
                <div className="text-right" data-id="element-3517">
                  <div className="text-sm font-medium text-gray-700" data-id="element-3518">
                    {mod.familyCount} fam
                  </div>
                  <div className="text-xs text-gray-500" data-id="element-3519">
                    {mod.totalHints} hints
                  </div>
                </div>
              </div>
            </Card>)}
        </div>
      </div>

      {/* Stats Row */}
      <div className="grid grid-cols-2 sm:grid-cols-6 gap-3 sm:gap-4 mb-6" data-id="element-3520">
        <StatCard label="Coverage" value={`${stats.overallCoverage}%`} icon={<BookOpen className="w-5 h-5" data-id="element-3522" />} color={stats.overallCoverage === 100 ? 'emerald' : stats.overallCoverage >= 80 ? 'amber' : 'blue'} data-id="element-3521" />
        <StatCard label="In Registry" value={stats.inRegistry} icon={<CheckCircle2 className="w-5 h-5" data-id="element-3524" />} color="emerald" data-id="element-3523" />
        <StatCard label="Totaal Hints" value={stats.totalHints} icon={<Lightbulb className="w-5 h-5" data-id="element-3526" />} color="blue" data-id="element-3525" />
        <StatCard label="Verrijkt (≥2 hints)" value={stats.enrichedHints} icon={<CheckCircle2 className="w-5 h-5" data-id="element-3528" />} color="emerald" data-id="element-3527" />
        <StatCard label="Schaars (1 hint)" value={stats.sparseHints} icon={<AlertTriangle className="w-5 h-5" data-id="element-3530" />} color="amber" data-id="element-3529" />
        <StatCard label="Geen Hints" value={stats.noHints} icon={<XCircle className="w-5 h-5" data-id="element-3532" />} color="red" data-id="element-3531" />
      </div>

      {/* Quick Filters */}
      <div className="flex flex-wrap items-center gap-2 mb-3" data-id="element-3533">
        <span className="text-xs font-medium text-gray-500 mr-1" data-id="element-3534">
          Snelfilters:
        </span>
        <button onClick={() => {
        setCoverageFilter('none');
        setHintFilter('all');
        setModuleFilter('all');
      }} className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${coverageFilter === 'none' ? 'bg-blue-100 text-blue-700 border border-blue-200' : 'bg-gray-100 text-gray-600 hover:bg-gray-200 border border-transparent'}`} data-id="element-3535">
          Ontbrekend
        </button>
        <button onClick={() => {
        setCoverageFilter('partial');
        setHintFilter('all');
        setModuleFilter('all');
      }} className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${coverageFilter === 'partial' ? 'bg-blue-100 text-blue-700 border border-blue-200' : 'bg-gray-100 text-gray-600 hover:bg-gray-200 border border-transparent'}`} data-id="element-3536">
          Onvolledig
        </button>
        <button onClick={() => {
        setHintFilter('none');
        setCoverageFilter('all');
        setModuleFilter('all');
      }} className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${hintFilter === 'none' ? 'bg-blue-100 text-blue-700 border border-blue-200' : 'bg-gray-100 text-gray-600 hover:bg-gray-200 border border-transparent'}`} data-id="element-3537">
          Geen hints
        </button>
        <button onClick={() => {
        setCoverageFilter('all');
        setHintFilter('all');
        setModuleFilter('all');
        setSearchQuery('');
      }} className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${coverageFilter === 'all' && hintFilter === 'all' && moduleFilter === 'all' && searchQuery === '' ? 'bg-blue-100 text-blue-700 border border-blue-200' : 'bg-gray-100 text-gray-600 hover:bg-gray-200 border border-transparent'}`} data-id="element-3538">
          Alles
        </button>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 mb-4 sm:mb-6 bg-white p-3 sm:p-4 rounded-xl border border-gray-200 shadow-sm" data-id="element-3539">
        <div className="flex-1" data-id="element-3540">
          <Input type="text" placeholder="Zoek op Family ID..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} icon={<Search className="w-4 h-4" data-id="element-3542" />} variant="admin" size="sm" data-id="element-3541" />
        </div>
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2" data-id="element-3543">
          <div className="hidden sm:block" data-id="element-3544">
            <Filter className="w-4 h-4 text-gray-400" data-id="element-3545" />
          </div>
          <Select value={moduleFilter} onChange={e => setModuleFilter(e.target.value)} options={[{
          value: 'all',
          label: 'Alle domeinen'
        }, ...modules.map(m => ({
          value: m,
          label: m
        }))]} variant="admin" size="sm" fullWidth={false} data-id="element-3546" />
          <Select value={coverageFilter} onChange={e => setCoverageFilter(e.target.value as typeof coverageFilter)} options={[{
          value: 'all',
          label: 'Alle dekkingen'
        }, {
          value: 'full',
          label: 'Volledig (100%)'
        }, {
          value: 'partial',
          label: 'Gedeeltelijk (1-99%)'
        }, {
          value: 'none',
          label: 'Geen (0%)'
        }]} variant="admin" size="sm" fullWidth={false} data-id="element-3547" />
          <Select value={hintFilter} onChange={e => setHintFilter(e.target.value as typeof hintFilter)} options={[{
          value: 'all',
          label: 'Alle hints'
        }, {
          value: 'enriched',
          label: 'Verrijkt (≥2)'
        }, {
          value: 'sparse',
          label: 'Schaars (1)'
        }, {
          value: 'none',
          label: 'Geen hints'
        }]} variant="admin" size="sm" fullWidth={false} data-id="element-3548" />
        </div>
      </div>

      <div className="flex flex-col lg:flex-row gap-4 sm:gap-6" data-id="element-3549">
        {/* Table */}
        <div className={`flex-1 transition-all ${selectedFamily ? 'lg:w-2/3' : 'w-full'}`} data-id="element-3550">
          <DataTable columns={columns} data={filteredData} onRowClick={setSelectedFamily} emptyMessage="Geen families gevonden met deze filters." data-id="element-3551" />
        </div>

        {/* Preview Panel */}
        {selectedFamily && <div className="w-full lg:w-1/3 flex-shrink-0" data-id="element-3552">
            <Card className="sticky top-6 flex flex-col max-h-[calc(100vh-120px)] shadow-sm" data-id="element-3553">
              <div className="p-4 border-b border-gray-100 flex items-center justify-between bg-gray-50 rounded-t-xl" data-id="element-3554">
                <div data-id="element-3555">
                  <h3 className="font-semibold text-gray-900 flex items-center gap-2" data-id="element-3556">
                    <FileText className="w-4 h-4 text-blue-500" data-id="element-3557" />
                    Content Preview
                  </h3>
                  <p className="text-xs font-mono text-gray-500 mt-1" data-id="element-3558">
                    {selectedFamily.familyId}
                  </p>
                </div>
                <button onClick={() => setSelectedFamily(null)} className="p-1.5 h-auto text-gray-400 hover:text-gray-600 hover:bg-gray-200 rounded-md transition-colors" data-id="element-3559">
                  <X className="w-4 h-4" data-id="element-3560" />
                </button>
              </div>

              <div className="p-4 overflow-y-auto flex-1 space-y-6" data-id="element-3561">
                {/* Registry status */}
                <div className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium ${selectedFamily.inRegistry ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : 'bg-red-50 text-red-700 border border-red-200'}`} data-id="element-3562">
                  {selectedFamily.inRegistry ? <>
                      <CheckCircle2 className="w-4 h-4" data-id="element-3563" /> In
                      FAMILY_SUPPORT_REGISTRY
                    </> : <>
                      <XCircle className="w-4 h-4" data-id="element-3564" /> Niet in registry — geen
                      support content
                    </>}
                </div>

                {/* Family Context */}
                <div data-id="element-3565">
                  <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2" data-id="element-3566">
                    Family Context
                  </h4>
                  <div className="flex flex-wrap gap-2" data-id="element-3567">
                    <Badge variant="secondary" data-id="element-3568">
                      Tier {selectedFamily.tier}
                    </Badge>
                    <Badge variant="secondary" data-id="element-3569">
                      {selectedFamily.questionCount} Vragen
                    </Badge>
                    <Badge variant="secondary" data-id="element-3570">{selectedFamily.module}</Badge>
                  </div>
                </div>

                {/* Example Question */}
                {exampleQuestion && <div data-id="element-3571">
                    <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2" data-id="element-3572">
                      Voorbeeld Vraag
                    </h4>
                    <div className="bg-gray-50 p-3 rounded-lg border border-gray-200" data-id="element-3573">
                      <p className="text-sm text-gray-800 leading-relaxed mb-2" data-id="element-3574">
                        {exampleQuestion.question}
                      </p>
                      <div className="flex flex-wrap gap-1.5" data-id="element-3575">
                        <Badge variant="secondary" data-id="element-3576">
                          {exampleQuestion.questionType}
                        </Badge>
                        <Badge variant="secondary" data-id="element-3577">
                          {exampleQuestion.levelCode}
                        </Badge>
                      </div>
                    </div>
                  </div>}

                <div data-id="element-3578">
                  <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2 flex items-center gap-2" data-id="element-3579">
                    {renderCheck(selectedFamily.support.coverage.hasExplanation)}{' '}
                    Uitleg (Zo werkt het)
                  </h4>
                  {selectedFamily.support.explanation ? <div className="bg-blue-50/50 p-3 rounded-lg border border-blue-100" data-id="element-3580">
                      <p className="text-sm font-medium text-blue-900 mb-1" data-id="element-3581">
                        {selectedFamily.support.explanation.title}
                      </p>
                      <p className="text-sm text-blue-800 whitespace-pre-wrap" data-id="element-3582">
                        {selectedFamily.support.explanation.body}
                      </p>
                    </div> : <p className="text-sm text-gray-400 italic" data-id="element-3583">
                      Geen uitleg beschikbaar.
                    </p>}
                </div>

                <div data-id="element-3584">
                  <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2 flex items-center gap-2" data-id="element-3585">
                    {renderCheck(selectedFamily.support.coverage.hasSteps)}{' '}
                    Stappenplan
                  </h4>
                  {selectedFamily.support.steps.length > 0 ? <ol className="list-decimal list-inside space-y-1" data-id="element-3586">
                      {selectedFamily.support.steps.map((step, i) => <li key={i} className="text-sm text-gray-700" data-id="element-3587">
                          {step}
                        </li>)}
                    </ol> : <p className="text-sm text-gray-400 italic" data-id="element-3588">
                      Geen stappenplan beschikbaar.
                    </p>}
                </div>

                <div data-id="element-3589">
                  <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2 flex items-center gap-2" data-id="element-3590">
                    {renderHintCount(selectedFamily.hintCount)} Hints (
                    {selectedFamily.hintCount})
                  </h4>
                  {selectedFamily.registryHints.length > 0 ? <div className="space-y-2" data-id="element-3591">
                      {selectedFamily.registryHints.map((hint, i) => <div key={i} className="bg-amber-50 p-3 rounded-lg border border-amber-100" data-id="element-3592">
                          {hint.title && <p className="text-xs font-bold text-amber-800 uppercase tracking-wider mb-1" data-id="element-3593">
                              {hint.title}
                            </p>}
                          <p className="text-sm text-amber-900 whitespace-pre-wrap" data-id="element-3594">
                            {hint.content}
                          </p>
                          {hint.visual && <p className="text-xs text-amber-600 mt-1 flex items-center gap-1" data-id="element-3595">
                              <span className="inline-block w-2 h-2 rounded-full bg-amber-400" data-id="element-3596" />
                              Visueel: {hint.visual.type}
                            </p>}
                        </div>)}
                    </div> : <p className="text-sm text-gray-400 italic" data-id="element-3597">
                      Geen hints beschikbaar.
                    </p>}
                </div>

                <div data-id="element-3598">
                  <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2 flex items-center gap-2" data-id="element-3599">
                    {renderCheck(selectedFamily.support.coverage.hasCheatSheet)}{' '}
                    Spiekbriefje
                  </h4>
                  {selectedFamily.support.cheatSheet ? <div className="bg-emerald-50/50 p-3 rounded-lg border border-emerald-100" data-id="element-3600">
                      <p className="text-sm font-medium text-emerald-900 mb-1" data-id="element-3601">
                        {selectedFamily.support.cheatSheet.title}
                      </p>
                      <p className="text-sm text-emerald-800 whitespace-pre-wrap" data-id="element-3602">
                        {selectedFamily.support.cheatSheet.body}
                      </p>
                      {selectedFamily.support.cheatSheet.rules && selectedFamily.support.cheatSheet.rules.length > 0 && <ul className="list-disc list-inside mt-2 text-sm text-emerald-800" data-id="element-3603">
                            {selectedFamily.support.cheatSheet.rules.map((rule, i) => <li key={i} data-id="element-3604">{rule}</li>)}
                          </ul>}
                    </div> : <p className="text-sm text-gray-400 italic" data-id="element-3605">
                      Geen spiekbriefje beschikbaar.
                    </p>}
                </div>

                <div data-id="element-3606">
                  <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2 flex items-center gap-2" data-id="element-3607">
                    {renderCheck(selectedFamily.support.coverage.hasAfterSummary)}{' '}
                    Sessie Samenvatting
                  </h4>
                  {selectedFamily.support.afterSummary ? <div className="bg-purple-50 p-3 rounded-lg border border-purple-100" data-id="element-3608">
                      <p className="text-sm text-purple-900" data-id="element-3609">
                        {selectedFamily.support.afterSummary}
                      </p>
                    </div> : <p className="text-sm text-gray-400 italic" data-id="element-3610">
                      Geen samenvatting beschikbaar.
                    </p>}
                </div>
              </div>
            </Card>
          </div>}
      </div>
    </PageLayout>;
}